import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { RpcError } from 'grpc-web'
import { ListControlsRequest } from '@trustero/trustero-api-web/lib/model/control_pb'
import { useIsShowModal } from 'src/Modal/ModalStateContext'
import { ModalFormId } from 'src/components/ModalForms'
import { UploadCSVModal } from 'src/components/ModalForms/Reusable/UploadCSVModal'
import { CSV_UPLOAD_TYPES } from 'src/components/ModalForms/Reusable/UploadCSVModal.constants'
import { useControls } from 'src/components/async/model/control/useControls'
import { useFrameworks } from 'src/components/async/Framework/useFrameworks'
import { useComplianceFrameworks } from 'src/components/async/complianceframework/useComplianceFrameworks'
import { FrameworksAbsoluteRoutes } from 'src/components/Reusable/RootPage/RootPage.constants'
import { useCustomFrameworkWizard } from 'src/pages/Frameworks/context/CustomFrameworkWizardContext'
import { useCreateCustomFrameworkWithRequirements } from 'src/pages/Frameworks/frameworks.hooks'
import {
  FRAMEWORK_REQUIREMENT_CSV_HEADERS,
  FRAMEWORK_REQUIREMENT_CSV_TEMPLATE,
  ImportFrameworkRequirementsCSVRow,
} from './FrameworkRequirementModals.constants'
import {
  getFrameworkRequirementCsvParsers,
  getFrameworkRequirementRowValidators,
  parseDuplicateRequirementNameError,
} from './FrameworkRequirementModals.helpers'

export const UploadFrameworkRequirementsCSVModal = (): JSX.Element => {
  const navigate = useNavigate()
  const { pageContext } = useParams()
  const submitWithRequirements = useCreateCustomFrameworkWithRequirements()
  const { name, description, groups, appendComplianceFrameworkModelId, reset } =
    useCustomFrameworkWizard()
  const submitLabel = appendComplianceFrameworkModelId
    ? 'Add requirements'
    : 'Create framework'
  const show = useIsShowModal(ModalFormId.UPLOAD_FRAMEWORK_REQUIREMENTS_CSV)
  const { data: controlsData, isLoading: controlsLoading } = useControls(
    new ListControlsRequest(),
    show,
  )
  const { data: frameworksData, loading: frameworksLoading } = useFrameworks()
  const {
    data: complianceFrameworksData,
    loading: complianceFrameworksLoading,
  } = useComplianceFrameworks()

  const validControlIdSet = useMemo(
    () => new Set((controlsData?.getItemsList() ?? []).map((c) => c.getId())),
    [controlsData],
  )

  const appendComplianceFrameworkId = useMemo(() => {
    if (!appendComplianceFrameworkModelId) return ''
    return (
      (complianceFrameworksData?.getItemsList() ?? [])
        .find(
          (framework) =>
            framework.getModelId() === appendComplianceFrameworkModelId,
        )
        ?.getId() ?? ''
    )
  }, [complianceFrameworksData, appendComplianceFrameworkModelId])

  const existingRequirementNames = useMemo(() => {
    if (!appendComplianceFrameworkId) return []
    return Array.from(
      new Set(
        (frameworksData?.getItemsList() ?? [])
          .filter(
            (f) => f.getComplianceFrameworkId() === appendComplianceFrameworkId,
          )
          .map((f) => f.getName().trim().toLowerCase())
          .filter(Boolean),
      ),
    )
  }, [frameworksData, appendComplianceFrameworkId])

  const parsers = useMemo(
    () =>
      getFrameworkRequirementCsvParsers(
        validControlIdSet,
        existingRequirementNames,
      ),
    [validControlIdSet, existingRequirementNames],
  )

  const uploadItemsFunc = async (
    items: ImportFrameworkRequirementsCSVRow[],
    _file?: File | null,
    setErrors?: React.Dispatch<React.SetStateAction<string[]>>,
  ): Promise<void> => {
    if (!appendComplianceFrameworkModelId && !name.trim()) {
      setErrors?.([
        'Framework name is missing. Go back to the first step and enter a name.',
      ])
      return
    }
    try {
      const { complianceFrameworkRecordId } = await submitWithRequirements({
        name: name.trim(),
        description,
        requirements: items.map((row) => ({
          name: row.name,
          category: row.group,
          groupDescription: row.groupDescription,
          description: row.description,
          controlIds: row.controlMappings,
        })),
        appendComplianceFrameworkModelId,
        groups,
      })
      reset()
      navigate(
        `/${pageContext}/${FrameworksAbsoluteRoutes.SHOW}/${complianceFrameworkRecordId}`,
      )
    } catch (err) {
      const dupInfo = parseDuplicateRequirementNameError(
        (err as RpcError)?.message ?? '',
      )
      if (dupInfo) {
        setErrors?.([
          dupInfo.isExisting
            ? `A requirement named "${dupInfo.name}" already exists in this framework.`
            : `Duplicate requirement name "${dupInfo.name}" in the uploaded CSV.`,
        ])
        return
      }
      throw err
    }
  }

  const formatSampleText = (
    <>
      {FRAMEWORK_REQUIREMENT_CSV_HEADERS.map((header, index) => (
        <React.Fragment key={header.output}>
          {header.optional ? header.input : <b>{header.input}</b>}
          {index < FRAMEWORK_REQUIREMENT_CSV_HEADERS.length - 1 ? ', ' : ''}
        </React.Fragment>
      ))}
    </>
  )

  const requirementsLoading =
    !!appendComplianceFrameworkModelId &&
    (frameworksLoading || complianceFrameworksLoading)

  if (!show || controlsLoading || !controlsData || requirementsLoading) {
    return (
      <UploadCSVModal<ImportFrameworkRequirementsCSVRow>
        modalId={ModalFormId.UPLOAD_FRAMEWORK_REQUIREMENTS_CSV}
        prevModalId={ModalFormId.ADD_UPLOAD_FRAMEWORK_REQUIREMENTS}
        uploadItemsFunc={async (): Promise<void> => undefined}
        csvHeaders={FRAMEWORK_REQUIREMENT_CSV_HEADERS}
        csvColumnParsers={parsers}
        rowValidators={getFrameworkRequirementRowValidators()}
        titleText="Upload framework requirements"
        description="Upload a CSV to add requirements to this framework. Control mappings in each row are optional; use comma-separated control IDs (the internal control row id for each control)."
        instruction="Loading existing framework requirements before CSV validation..."
        formatSampleText={formatSampleText}
        submitText={submitLabel}
        csvTemplate={FRAMEWORK_REQUIREMENT_CSV_TEMPLATE}
        uploadType={CSV_UPLOAD_TYPES.DEFAULT}
        csvFileName="framework_requirements_example"
        showCustomForm
        customForm={
          <p>
            Loading existing framework requirements so duplicate-name checks
            work before upload.
          </p>
        }
        customSubmitButton={
          <button type="button" disabled>
            {submitLabel}
          </button>
        }
      />
    )
  }

  return (
    <UploadCSVModal<ImportFrameworkRequirementsCSVRow>
      modalId={ModalFormId.UPLOAD_FRAMEWORK_REQUIREMENTS_CSV}
      prevModalId={ModalFormId.ADD_UPLOAD_FRAMEWORK_REQUIREMENTS}
      uploadItemsFunc={uploadItemsFunc}
      csvHeaders={FRAMEWORK_REQUIREMENT_CSV_HEADERS}
      csvColumnParsers={parsers}
      rowValidators={getFrameworkRequirementRowValidators()}
      titleText="Upload framework requirements"
      description="Upload a CSV to add requirements to this framework. Control mappings in each row are optional; use comma-separated control IDs (the internal control row id for each control)."
      instruction="Your CSV should have one row per requirement with these columns (bold = required):"
      formatSampleText={formatSampleText}
      submitText={submitLabel}
      csvTemplate={FRAMEWORK_REQUIREMENT_CSV_TEMPLATE}
      uploadType={CSV_UPLOAD_TYPES.DEFAULT}
      csvFileName="framework_requirements_example"
    />
  )
}
